import { useState, useEffect } from "react";
import { Card, Table, Typography, Select, Spin, Row, Col, Button, Switch, InputNumber, message } from "antd";
import { LoadingOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import Axios from "axios";
import { getOAuthHeaders } from "../../../constants/oAuthValidation";

const ManageCafeteriaMenuItems = () => {
  const [loading, setLoading] = useState(false);
  const [menuDetails, setMenuDetails] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedBunitId, setSelectedBunitId] = useState(null);
  const [selectedCatalogueId, setSelectedCatalogueId] = useState(null);
  const [selectedCatalogueName, setSelectedCatalogueName] = useState(null);
  const [view, setView] = useState("catalogue"); // "catalogue" or "menu"

  const serverUrl = process.env.REACT_APP_serverUrl;
  const { access_token } = getOAuthHeaders();

  // Fetch cafeteria menu items
  const getCafeteriaMenuItems = async () => {
    setLoading(true);
    try {
      const response = await Axios.post(
        serverUrl,
        {
          query: `
            query {
              getCafeteriaMenuItems(from: 0, limit: 2000) {
                mProductId
                csClientId
                csBunitId
                cwrPcatalogueId
                cwrPcatalogueName
                created
                createdby
                updated
                isactive
                updatedby
                sunitprice
                slistprice
                onhandQty
                description
                isPromoApplicable
                isavailable
                ismonday
                istuesday
                iswednesday
                isthursday
                isfriday
                issaturday
                issunday
                colorcode
                preparationTime
                cwrMenuTimeslot {
                  cwrMenuTimeslotId
                  name
                  startTime
                  endTime
                }
                mProduct {
                  csBunitId
                  bunitName
                  imageUrl
                  value
                  name
                  name2
                  type
                  csTaxcategoryId
                  mProductCategoryId
                  productCategoryName
                  csUomId
                  uomName
                  upc
                  batchedProduct
                  isManualQty
                  isDecimal
                  imageurl
                  shortDescription
                  hsncode
                  returnable
                  returnDays
                  description
                  batchedForSale
                  batchedForStock
                  multiPrice
                  addNewLine
                  shelfLife
                  isBag
                  showAddons
                  foodType
                  productSegment
                  taxCategory {
                    csTaxcategoryID
                    name
                    overRideTax
                    overRideCondition
                    contraTaxCategoryId
                    contraTaxCategory {
                      contraTaxCategoryName
                      contraTaxId
                      contraTaxName
                      contraRate
                    }
                  }
                  mBatch {
                    mBatchId
                    batchno
                    upc
                    price
                    listPrice
                    startdate
                    enddate
                    life
                  }
                  productGroup {
                    mProductGroupId
                    name
                    value
                    description
                  }
                  productAddons {
                    mProductAddOnId
                    name
                    price
                    mAddonGroup {
                      mAddonGroupId
                      name
                      maxqty
                      minqty
                      type
                    }
                    mAddonProduct {
                      mProductId
                      name
                    }
                  }
                  productAttributes {
                    mProductAttributeId
                    value
                    mAttributeGroup {
                      mAttributeGroupId
                      name
                    }
                    mAttribute {
                      mAttributeId
                      name
                    }
                  }
                  productManufacturer {
                    mProductManufacturerId
                    name
                    value
                    description
                  }
                  productBrand {
                    brandId
                    name
                    value
                    description
                  }
                  priceLists {
                    pricelist
                    pricestd
                    sPricelistId
                    sPriceList
                  }
                  customAttributes {
                    name
                    fileds {
                      name
                      type
                      values
                      defaultValue
                      mandatory
                    }
                  }
                }
              }
            }
          `,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access_token}`,
          },
        }
      );
      const items = response.data.data.getCafeteriaMenuItems;
      setMenuDetails(items);

      // Extract unique business units
      const bUnits = [...new Set(items.map((item) => ({
        csBunitId: item.csBunitId,
        bunitName: item.mProduct.bunitName,
      })))]
        .filter((bUnit, index, self) => 
          index === self.findIndex((b) => b.csBunitId === bUnit.csBunitId)
        )
        .map((bUnit) => ({
          value: bUnit.csBunitId,
          label: bUnit.bunitName,
        }));
      setBusinessUnits(bUnits);
    } catch (error) {
      console.error("Error fetching cafeteria menu items:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update cafeteria menu item (qtyCount or isactive)
  const updateCafeteriaMenuItem = async (catalogueId, productId, qtyCount, isActive) => {
    setLoading(true);
    try {
      const currentItem = menuDetails.find(
        (item) => item.cwrPcatalogueId === catalogueId && item.mProductId === productId
      );
      if (!currentItem) {
        message.error("Item not found for update");
        return;
      }
  
      const updatedQtyCount = qtyCount !== undefined ? qtyCount : currentItem.onhandQty;
      const updatedIsActive = isActive !== undefined ? isActive : currentItem.isactive;
  
      const response = await Axios.post(
        serverUrl,
        {
          query: `
            mutation {
              upsertCafeteriaMenuItem(menuItems: {
                catalogueId: "${catalogueId}"
                lines: [{
                  mProductId: "${productId}"
                  qtyCount: ${updatedQtyCount}
                  isactive: ${updatedIsActive}
                }]
              }) {
                status
                message
              }
            }
          `,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access_token}`,
          },
        }
      );
  
      const result = response.data?.data?.upsertCafeteriaMenuItem;
  
      if (result?.status === "200") {
        setMenuDetails((prev) =>
          prev.map((item) =>
            item.cwrPcatalogueId === catalogueId && item.mProductId === productId
              ? {
                  ...item,
                  onhandQty: updatedQtyCount,
                  isactive: updatedIsActive,
                }
              : item
          )
        );
        message.success(result.message || "Updated successfully");
      } else {
        message.error(result?.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating cafeteria menu item:", error);
      message.error("Something went wrong while updating");
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    getCafeteriaMenuItems();
  }, [serverUrl]);

  // Get unique catalogues with names
  const getCatalogues = () => {
    const catalogueMap = new Map();
    menuDetails.forEach((item) => {
      if (!catalogueMap.has(item.cwrPcatalogueId)) {
        catalogueMap.set(item.cwrPcatalogueId, {
          cwrPcatalogueId: item.cwrPcatalogueId,
          cwrPcatalogueName: item.cwrPcatalogueName || item.cwrPcatalogueId,
        });
      }
    });
    return Array.from(catalogueMap.values());
  };

  // Get business units for the selected catalogue
  const getBusinessUnitsForCatalogue = () => {
    if (!selectedCatalogueId) return [];
    const bUnits = [...new Set(
      menuDetails
        .filter((item) => item.cwrPcatalogueId === selectedCatalogueId)
        .map((item) => ({
          csBunitId: item.csBunitId,
          bunitName: item.mProduct.bunitName,
        }))
    )]
      .filter((bUnit, index, self) => 
        index === self.findIndex((b) => b.csBunitId === bUnit.csBunitId)
      )
      .map((bUnit) => ({
        value: bUnit.csBunitId,
        label: bUnit.bunitName,
      }));
    return bUnits;
  };

  // Group menu items by category for the selected business unit and catalogue
  const getFilteredMenuItems = () => {
    if (!selectedBunitId || !selectedCatalogueId) return {};

    const filteredItems = menuDetails.filter(
      (item) => item.cwrPcatalogueId === selectedCatalogueId && item.csBunitId === selectedBunitId
    );

    return filteredItems.reduce((acc, item) => {
      const category = item.mProduct.productCategoryName || "Uncategorized";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});
  };

  // Handle catalogue selection and auto-select first business unit
  const handleManageMenu = (catalogueId, catalogueName) => {
    setSelectedCatalogueId(catalogueId);
    setSelectedCatalogueName(catalogueName);
    setView("menu");
    // Reset selected business unit
    setSelectedBunitId(null);
    // Get business units for the selected catalogue
    const bUnits = [...new Set(
      menuDetails
        .filter((item) => item.cwrPcatalogueId === catalogueId)
        .map((item) => ({
          csBunitId: item.csBunitId,
          bunitName: item.mProduct.bunitName,
        }))
    )]
      .filter((bUnit, index, self) => 
        index === self.findIndex((b) => b.csBunitId === bUnit.csBunitId)
      )
      .map((bUnit) => ({
        value: bUnit.csBunitId,
        label: bUnit.bunitName,
      }));
    // Auto-select first business unit if available
    if (bUnits.length > 0) {
      setSelectedBunitId(bUnits[0].value);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: ["mProduct", "name"],
      key: "name",
    },
    {
      title: "Product Code",
      dataIndex: ["mProduct", "value"],
      key: "value",
    },
    {
      title: "Price",
      dataIndex: "slistprice",
      key: "price",
      render: (price) => {
        if (price === null || price === undefined || isNaN(price)) {
          return "N/A";
        }
        return `₹${parseFloat(price).toFixed(2)}`;
      },
    },
    {
      title: "On Hand Qty",
      dataIndex: "onhandQty",
      key: "onhandQty",
      render: (qty, record) => {
        let tempValue = qty;
    
        return (
          <InputNumber
            min={0}
            defaultValue={qty}
            onChange={(value) => {
              tempValue = value;
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && tempValue !== qty) {
                updateCafeteriaMenuItem(record.cwrPcatalogueId, record.mProductId, tempValue, undefined);
              }
            }}
          />
        );
      },
    },    
    {
      title: "Tax",
      dataIndex: ["mProduct", "taxCategory", "name"],
      key: "tax",
    },
    {
      title: "Tax Rate",
      dataIndex: ["mProduct", "taxCategory", "contraTaxCategory", "contraRate"],
      key: "taxRate",
      render: (rate) => (rate ? `${rate}%` : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "isactive",
      key: "status",
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={(checked) => {
            updateCafeteriaMenuItem(record.cwrPcatalogueId, record.mProductId, undefined, checked);
          }}
        />
      ),
    },
    {
      title: "Time Slot",
      key: "timeSlot",
      width:200,
      render: (_, record) => {
        const timeslot = record.cwrMenuTimeslot;
        if (timeslot && timeslot.name) {
          return (
            <>
              <div>{timeslot.name}</div>
              <div>{timeslot.startTime} - {timeslot.endTime}</div>
            </>
          );
        }
        return "N/A";
      },
    }
    
  ];

  const catalogues = getCatalogues();
  const filteredBusinessUnits = getBusinessUnitsForCatalogue();
  const groupedMenuItems = getFilteredMenuItems();

  return (
    <Spin
      indicator={<LoadingOutlined style={{ fontSize: 36 }} className="spinLoader" spin />}
      spinning={loading}
    >
      <div style={{height:"80vh",overflowY:"scroll"}}>
        {view === "catalogue" ? (
          <>
            {/* Header */}
            <Row justify="start" align="middle" style={{ marginBottom: "1em" }}>
              <Col>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  Manage Cafeteria Menu Items
                </Typography.Title>
              </Col>
            </Row>

            {/* Catalogue Cards */}
            <Row gutter={[16, 16]}>
              {catalogues.length > 0 ? (
                catalogues.map((catalogue) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={catalogue.cwrPcatalogueId}>
                    <Card
                      style={{
                        textAlign: "left",
                        borderRadius: "8px",
                        padding: "1em",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column"
                    }}
                    >
                      <Typography.Title level={4}>
                        {catalogue.cwrPcatalogueName}
                      </Typography.Title>
                      <div style={{ marginTop: "1em" }}>
                        <Button
                          type="primary"
                          style={{ backgroundColor: "black", borderRadius: "8px" }}
                          onClick={() => handleManageMenu(catalogue.cwrPcatalogueId, catalogue.cwrPcatalogueName)}
                        >
                          Manage Menu
                        </Button>
                      </div>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col span={24}>
                  <Typography.Text>No catalogues found.</Typography.Text>
                </Col>
              )}
            </Row>
          </>
        ) : (
          <>
            {/* Header with Back Button and Business Unit Select */}
            <Row justify="space-between" align="middle" style={{ marginBottom: "1em" }}>
              <Col>
                <Button
                  onClick={() => {
                    setView("catalogue");
                    setSelectedBunitId(null);
                    setSelectedCatalogueName(null);
                  }}
                  icon={<ArrowLeftOutlined />}
                  style={{ borderRadius: "8px", backgroundColor: "black", color: "#fff" }}
                >
                  Back to Catalogues
                </Button>
              </Col>
              <Col>
                <Typography.Text strong>Business Unit: </Typography.Text>
                <Select
                  value={selectedBunitId}
                  onChange={(value) => setSelectedBunitId(value)}
                  style={{ width: 250 }}
                  placeholder="Select a business unit"
                  options={filteredBusinessUnits}
                />
              </Col>
            </Row>
            <Row style={{ marginBottom: "1em" }}>
              <Col>
                <Typography.Title level={2} style={{ margin: 0 }}>
                  {selectedCatalogueName}
                </Typography.Title>
              </Col>
            </Row>

            {/* Category-wise Tables */}
            {selectedBunitId && Object.entries(groupedMenuItems).length > 0 ? (
              Object.entries(groupedMenuItems).map(([categoryName, items]) => (
                <Card
                  key={categoryName}
                  style={{ marginBottom: "20px", borderRadius: "8px", padding: "1em" }}
                >
                  <Typography.Title level={4}>{categoryName}</Typography.Title>
                  <Table
                    dataSource={items}
                    columns={columns}
                    rowKey="mProductId"
                    pagination={false}
                    scroll={{
                      y:"50vh"
                    }}
                  />
                </Card>
              ))
            ) : (
              <Typography.Text>
                {selectedBunitId
                  ? "No items found for the selected catalogue and business unit."
                  : "Please select a business unit."}
              </Typography.Text>
            )}
          </>
        )}
      </div>
    </Spin>
  );
};

export default ManageCafeteriaMenuItems;